import React, { useEffect, useState } from 'react';
import { Button, cn, Heading, Typography } from '@kiiwi/ui';
import Cookies from 'js-cookie';
import { useCurrentUser } from '@docavenue/components';
import { IS_PHONE_OR_TABLET, useMediaQueryBreakPoint } from '@/src/hooks';
import FontAwesomeIcon, {
  AcceptedIconNames,
} from '@/components/atoms/FontawesomeIcon/FontawesomeIcon';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/not-existing-kiiwi-components/Dialog';
import { PATIENT_APP_ANDROID_URL, PATIENT_APP_IOS_URL } from '@/src/constants';
import { useTranslation } from '@/src/i18n';
import { analyticsEvent } from '@/src/analytic_tag_manager';
import { isIOS, isProductionEnvironment } from '@/src/utils';

const DESCRIPTION_ITEMS: {
  icon: AcceptedIconNames;
  key: string;
}[] = [
  {
    icon: 'bell:regular',
    key: 'download_app_dialog_reminders_description',
  },
  {
    icon: 'user-doctor:regular',
    key: 'download_app_dialog_documents_and_meetings_description',
  },
  {
    icon: 'video:regular',
    key: 'download_app_dialog_tlc_description',
  },
];

const DownloadAppDialog = () => {
  const { t } = useTranslation();
  const {
    id: userId,
    deviceTokensByMobileInstance = {},
    userPatientInformation = {},
  } = useCurrentUser() || {};

  const [open, setOpen] = useState(false);

  const cookieKey = `downloadAppDialogDismissed-${userId}`;
  const isPhoneOrTablet = useMediaQueryBreakPoint(IS_PHONE_OR_TABLET);
  const isIOSDevice = isIOS();
  const isProduction = isProductionEnvironment();
  const isUserConfirmed =
    !!userId && (userPatientInformation?.userPatientProfiles || []).length > 0;

  const qrCodeSvg = `/static/images/download-app-with-qr-code-${
    isProduction ? 'prod' : 'staging'
  }.svg`;

  const onOpenChange = (value: boolean) => {
    if (!value) Cookies.set(cookieKey, 'true', { expires: 7 });
    setOpen(value);
  };

  const onClickDownload = () =>
    analyticsEvent({
      category: 'AppPromotion',
      action: 'ClickRedirectionStore',
      label: isIOSDevice ? 'AppStoreIOS' : 'PlayStoreAndroid',
    });

  useEffect(() => {
    if (isUserConfirmed) {
      const hasDeviceTokens =
        'APPLICATION_PATIENT' in deviceTokensByMobileInstance &&
        deviceTokensByMobileInstance.APPLICATION_PATIENT?.length > 0;
      const dismissedLately = Cookies.get(cookieKey) === 'true';
      if (!hasDeviceTokens && !dismissedLately) setOpen(true);
    }
  }, [isUserConfirmed, deviceTokensByMobileInstance]);

  useEffect(() => {
    const isQRCodeDisplayed = open && !isPhoneOrTablet;
    if (isQRCodeDisplayed)
      analyticsEvent({
        category: 'AppPromotion',
        action: 'DisplayQrCode',
      });
  }, [open, isPhoneOrTablet]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onInteractOutside={e => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{t('download_app_dialog_title')}</DialogTitle>
        </DialogHeader>
        <div
          className={cn(
            'flex items-center justify-center gap-6 px-4 py-3 text-neutral-primary',
            isPhoneOrTablet ? 'flex-col' : 'flex-row',
          )}
        >
          <div className="flex flex-col gap-4">
            {isPhoneOrTablet && (
              <img
                alt={t('download_app_dialog_illustration_alt')}
                src="/static/images/download-app.jpg"
                className="mx-auto h-[230px]"
              />
            )}

            <Heading
              as="h3"
              variant={isPhoneOrTablet ? 'title-2' : 'subtitle-2'}
              className={cn(isPhoneOrTablet && 'text-center')}
            >
              {t('download_app_dialog_subtitle')}
            </Heading>

            <div className="flex flex-col gap-2">
              {DESCRIPTION_ITEMS.map(item => (
                <div
                  key={`download-app-${item.key}`}
                  className="flex flex-row items-center gap-2"
                >
                  <FontAwesomeIcon name={item.icon} className="size-4" />
                  <Typography>{t(item.key)}</Typography>
                </div>
              ))}
            </div>
          </div>

          {isPhoneOrTablet ? (
            <Button className="w-full" asChild>
              <a
                target="_blank"
                href={
                  isIOSDevice ? PATIENT_APP_IOS_URL : PATIENT_APP_ANDROID_URL
                }
                rel="noopener noreferrer"
                onClick={onClickDownload}
              >
                {t('download_app_dialog_cta')}
              </a>
            </Button>
          ) : (
            <img
              alt={t('download_app_dialog_qrcode_alt')}
              src={qrCodeSvg}
              className="w-[220px]"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { DownloadAppDialog };
